// UI display types
export interface IStarDistributionBar {
  level: number
  count: number
  percentage: number
}

export function buildStarDistributionBars(
  dist: IGoogleReviewsStarDistribution,
  total: number
): IStarDistributionBar[] {
  return [5, 4, 3, 2, 1].map((level) => ({
    level,
    count: dist[`star${level}` as keyof IGoogleReviewsStarDistribution],
    percentage:
      total > 0 ? (dist[`star${level}` as keyof IGoogleReviewsStarDistribution] / total) * 100 : 0
  }))
}

// Shared
export interface IGoogleReviewsStarDistribution {
  star1: number
  star2: number
  star3: number
  star4: number
  star5: number
}

// Dashboard (endpoint #3)
export interface IGoogleReviewsLocationSummary {
  locationId: string
  locationName: string
  averageRating: number
  totalReviewCount: number
  starDistribution: IGoogleReviewsStarDistribution
}

export interface IGoogleReviewsSummaryRequest {
  locationIds: string[]
  from?: string
  to?: string
}

export interface IGoogleReviewsSummaryResponse {
  from: string
  to: string
  averageRating: number
  totalReviewCount: number
  starDistribution: IGoogleReviewsStarDistribution
  locations: IGoogleReviewsLocationSummary[]
}

// Detail (endpoint #2)
export interface IGoogleReviewItem {
  id: string
  reviewerName: string
  reviewerPhotoUrl: string
  starRating: number
  comment: string
  reviewTime: string
}

export interface IGoogleReviewsDetailRequest {
  locationId: string
  from?: string
  to?: string
}

export interface IGoogleReviewsDetailResponse {
  locationId: string
  locationName: string
  from: string
  to: string
  averageRating: number
  totalReviewCount: number
  starDistribution: IGoogleReviewsStarDistribution
  reviews: IGoogleReviewItem[]
}

// Trends
export interface IGoogleReviewsTrendsRequest {
  locationIds: string[]
}

export interface IMonthColumn {
  year: number
  month: number
  isCurrentMonth: boolean
}

export interface IMonthData {
  year: number
  month: number
  averageRating: number
  reviewCount: number
}

export interface IGoogleReviewsLocationTrend {
  locationId: string
  locationName: string
  monthlyData: IMonthData[]
}

export interface IGoogleReviewsTrendsResponse {
  months: IMonthColumn[]
  locations: IGoogleReviewsLocationTrend[]
}

export interface ITrendResult {
  slope: number
  direction: 'up' | 'down' | 'flat'
}

export function computeTrendSlope(values: number[]): ITrendResult {
  const n = values.length
  if (n < 2) return { slope: 0, direction: 'flat' }
  let sumX = 0
  let sumY = 0
  let sumXY = 0
  let sumX2 = 0
  for (let i = 0; i < n; i++) {
    sumX += i
    sumY += values[i]
    sumXY += i * values[i]
    sumX2 += i * i
  }
  const denom = n * sumX2 - sumX * sumX
  if (denom === 0) return { slope: 0, direction: 'flat' }
  const slope = (n * sumXY - sumX * sumY) / denom
  const rounded = Math.round(slope * 100) / 100
  const direction: ITrendResult['direction'] = rounded > 0.05 ? 'up' : rounded < -0.05 ? 'down' : 'flat'
  return { slope: rounded, direction }
}
